import { z } from 'zod'
import { LOCALES } from './constants'
import { DateOfBirthSchema } from './identity'
import { normalisePhone } from './phone'

export const LocaleSchema = z.enum(LOCALES)

export const LoginSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1),
  turnstileToken: z.string().optional(),
})
export type LoginInput = z.infer<typeof LoginSchema>

// The generated placeholder avatar shown until a member uploads a real
// photo (see POST /api/auth/me/avatar) -- rendered client-side by @dicebear, mirroring
// mik-ng's DicebearAvatarStyle so the same three styles are on offer.
export const AVATAR_STYLES = ['initials', 'avataaars', 'bottts'] as const
export const AvatarStyleSchema = z.enum(AVATAR_STYLES)
export type AvatarStyle = z.infer<typeof AvatarStyleSchema>

const emptyToNull = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? null : v)

// Stored as E.164 when it parses as a real number (see phone.ts).
const PhoneSchema = z.string().trim().max(40).transform((v) => normalisePhone(v))

export const UpdateProfileSchema = z
  .object({
    displayName: z.string().trim().min(1).max(120).optional(),
    phone: PhoneSchema.nullable().optional(),
    address: z.string().trim().max(200).nullable().optional(),
    postalCode: z.string().trim().max(20).nullable().optional(),
    city: z.string().trim().max(100).nullable().optional(),
    locale: LocaleSchema.optional(),
    // wp_usermeta.privacy: hides contact details from other members.
    privateDetails: z.boolean().optional(),
    avatarStyle: AvatarStyleSchema.optional(),
    // In case of emergency -- who the club should call, kept by the member
    // themselves rather than an admin, since it goes stale the moment they
    // do not update it.
    iceContactName: z.string().trim().max(120).nullable().optional(),
    iceContactPhone: PhoneSchema.nullable().optional(),
    // The licence NUMBER stays a profile field; expiries, ratings and the
    // medical live in member.user_licences (PUT /api/members/:id/licences).
    licenceNumber: z.string().trim().max(40).nullable().optional(),
    // An empty input means "none": the form sends '' for a cleared field.
    dateOfBirth: z.preprocess(emptyToNull, DateOfBirthSchema.nullable()).optional(),
    // What the member is called day to day; shown beside the name.
    nickname: z.preprocess(emptyToNull, z.string().trim().max(60).nullable()).optional(),
    // #225: switch off the licence and medical warning on the booking form.
    // Switching it off needs the member's explicit confirmation that they
    // are responsible for keeping both valid; switching it on does not.
    licenceNoticeOff: z.boolean().optional(),
    acceptsLicenceResponsibility: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' })
  .refine((v) => v.licenceNoticeOff !== true || v.acceptsLicenceResponsibility === true, {
    message: 'Confirm responsibility for licence and medical validity',
    path: ['acceptsLicenceResponsibility'],
  })
export type UpdateProfileInput = z.infer<typeof UpdateProfileSchema>

// What GET /api/auth/me returns, and what the frontend's AuthContext holds.
export const SessionUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string(),
  memberNumber: z.string().nullable(),
  locale: LocaleSchema,
  avatarStyle: AvatarStyleSchema,
  // Epoch ms of the uploaded photo, or null when there is none. It doubles
  // as the cache-buster on the image URL.
  avatarVersion: z.number().nullable(),
  roles: z.array(z.string()),
  permissions: z.array(z.string()),
})
export type SessionUser = z.infer<typeof SessionUserSchema>

// member.login_events.event.
export const LOGIN_EVENTS = [
  'login.success',
  'login.failure',
  'logout',
  'refresh.reuse',
  'session.revoked',
  'password.change',
  'password.reset',
  'passkey.add',
  'passkey.remove',
  'login_method.change',
] as const
export type LoginEvent = (typeof LOGIN_EVENTS)[number]

// Why a login failed. INTERNAL: stored for administrators, never returned
// to the client, which always gets one generic message.
export const LOGIN_FAILURE_REASONS = [
  'unknown_email',
  'bad_password',
  'no_password',
  'inactive',
  'turnstile',
  // An email link that is unknown, expired or already used.
  'bad_token',
  // A wrong 6-digit sign-in code, or one typed after the code ran out.
  'bad_code',
  // A passkey that is not registered, or whose signature did not verify.
  'bad_passkey',
] as const
export type LoginFailureReason = (typeof LOGIN_FAILURE_REASONS)[number]

// --- Sign-in methods -------------------------------------------------------
//
// How a member has chosen to sign in (member.users.login_method). A
// passwordless member has no password at all; a one-time email link and a
// registered passkey work whatever the choice. See V40__users.sql.
export const LOGIN_METHODS = ['password', 'magic_link', 'passkey'] as const
export type LoginMethod = (typeof LOGIN_METHODS)[number]
export const LoginMethodSchema = z.enum(LOGIN_METHODS)

// The password policy, one schema for the API and both SPAs. 10 characters
// is the floor (NIST SP 800-63B asks for 8, and nothing more elaborate:
// length beats composition rules). The ceiling is 72 BYTES, not only 128
// characters, because bcrypt ignores everything past its 72nd byte -- a
// longer password would silently be only as strong as its first 72 bytes.
export const PASSWORD_MIN_LENGTH = 10
export const PASSWORD_MAX_LENGTH = 128
export const PASSWORD_MAX_BYTES = 72

export type PasswordProblem = 'tooShort' | 'tooLong' | 'isEmail'

// UTF-8 length without TextEncoder, which this package's lib does not
// include: bcrypt counts bytes, and "ä" is two of them.
function utf8Length(value: string): number {
  let bytes = 0
  for (const ch of value) {
    const cp = ch.codePointAt(0)!
    bytes += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4
  }
  return bytes
}

// What is wrong with a proposed password, or null. `email` is the member's
// own address: neither it nor its local part may be the password.
export function passwordProblem(password: string, email?: string | null): PasswordProblem | null {
  if ([...password].length < PASSWORD_MIN_LENGTH) return 'tooShort'
  if ([...password].length > PASSWORD_MAX_LENGTH || utf8Length(password) > PASSWORD_MAX_BYTES) {
    return 'tooLong'
  }
  if (email) {
    const candidate = password.trim().toLowerCase()
    const address = email.trim().toLowerCase()
    if (candidate === address || candidate === address.split('@')[0]) return 'isEmail'
  }
  return null
}

// The shape alone; the email rule needs the member, so the API applies
// passwordProblem again once it knows who they are.
export const NewPasswordSchema = z.string().superRefine((value, ctx) => {
  const problem = passwordProblem(value)
  if (problem) ctx.addIssue({ code: 'custom', message: problem })
})

// Every request that emails a one-time link answers 202 with this, whether
// or not the address has an account -- nothing here tells an attacker
// which addresses are real.
export const EmailRequestSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(320),
  turnstileToken: z.string().optional(),
})
export type EmailRequestInput = z.infer<typeof EmailRequestSchema>

// The token from an emailed link: 43 characters of base64url (32 bytes).
export const EmailTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/)

export const ResetPasswordSchema = z.object({
  token: EmailTokenSchema,
  newPassword: NewPasswordSchema,
})
export type ResetPasswordInput = z.infer<typeof ResetPasswordSchema>

export const ChangePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(1024),
  newPassword: NewPasswordSchema,
})
export type ChangePasswordInput = z.infer<typeof ChangePasswordSchema>

export const MagicLinkVerifySchema = z.object({ token: EmailTokenSchema })

export const MagicCodeVerifySchema = z.object({
  email: z.string().trim().toLowerCase().email().max(320),
  // Six digits; spaces and dashes a member types between them are ignored.
  code: z
    .string()
    .transform((v) => v.replace(/[\s-]/g, ''))
    .pipe(z.string().regex(/^\d{6}$/)),
})
export type MagicCodeVerifyInput = z.infer<typeof MagicCodeVerifySchema>

// POST /api/auth/email-token/inspect: what a link is for, before the page
// asks the member to act on it. Only the holder of the link sees this.
export interface EmailTokenInfo {
  purpose: 'reset' | 'invite' | 'magic_link'
  email: string
  displayName: string
  expiresAt: string
}

export const SetLoginMethodSchema = z.object({ method: LoginMethodSchema })

export const PasskeyNameSchema = z.string().trim().min(1).max(60)
export const RenamePasskeySchema = z.object({ name: PasskeyNameSchema })

// The browser's WebAuthn response, as @simplewebauthn/browser serialises
// it. Checked properly by @simplewebauthn/server; this only bounds it.
export const WebAuthnResponseSchema = z
  .object({
    id: z.string().regex(/^[A-Za-z0-9_-]+$/).max(1366),
    rawId: z.string().max(1366),
    type: z.literal('public-key'),
    response: z.record(z.string(), z.unknown()),
    clientExtensionResults: z.record(z.string(), z.unknown()).default({}),
    authenticatorAttachment: z.enum(['platform', 'cross-platform']).optional(),
  })
  .passthrough()

export const PasskeyRegisterVerifySchema = z.object({
  response: WebAuthnResponseSchema,
  name: PasskeyNameSchema.optional(),
})
export const PasskeyLoginVerifySchema = z.object({ response: WebAuthnResponseSchema })

export interface Passkey {
  id: string
  name: string
  deviceType: 'singleDevice' | 'multiDevice'
  backedUp: boolean
  createdAt: string
  lastUsedAt: string | null
}

// GET /api/auth/sign-in: the profile page's "Sign-in method" card.
export interface SignInSettings {
  loginMethod: LoginMethod
  hasPassword: boolean
  passkeys: Passkey[]
}

// problem+json `code`s the password and sign-in-method endpoints answer
// with, so the UI can say what to do next in the member's own language:
//   reauth_required    sign in again first: adding a passkey or changing
//                      the sign-in method needs a sign-in from the last
//                      15 minutes
//   password_required  choose "password" only once a password is set --
//                      through the emailed link
//   passkey_required   choose "passkey" only once one is registered
//   last_passkey       the only passkey of a passkey member; switch first
//   wrong_password     the current password did not match
//   weak_password      the new password breaks the policy; `reason` says how
export const AUTH_PROBLEM_CODES = [
  'reauth_required',
  'password_required',
  'passkey_required',
  'last_passkey',
  'wrong_password',
  'weak_password',
] as const
export type AuthProblemCode = (typeof AUTH_PROBLEM_CODES)[number]

// Where a session or a sign-in came from, as the member and the admin app
// show it. The device class, browser and OS are parsed server-side from
// the User-Agent (apps/api/src/lib/clientInfo.ts).
export interface LoginClient {
  country: string | null
  city: string | null
  device: 'mobile' | 'tablet' | 'desktop' | 'bot' | null
  browser: string | null
  os: string | null
}

// GET /api/auth/sessions and the admin app's member dialog. `id` names
// the session for DELETE /api/auth/sessions/:id; `current` is only set on
// the member's own list.
export interface LoginSession extends LoginClient {
  id: number
  current?: boolean
  ip: string | null
  signedInAt: string
  lastUsedAt: string
}

// GET /api/auth/logins: the member's own history. No IP, no failure reason.
export interface OwnLoginEvent extends LoginClient {
  eventId: number
  occurredAt: string
  event: LoginEvent
}

// The admin app's view of a member.login_events row.
export interface AdminLoginEvent extends OwnLoginEvent {
  userId: string | null
  failureReason: LoginFailureReason | null
  emailHash: string | null
  ip: string | null
  region: string | null
  asOrg: string | null
  displayName?: string | null
}
